import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@clouway/ui'

import { useSession } from '@/features/auth'

// First letter of the first and last word (or first two letters of a single
// word) — the avatar fallback when there is no image.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Shows who/which tenant is signed in (the app gave no such cue before) and
// lets the user sign out — e.g. to sign back in under a different tenant.
export function UserMenu() {
  const { t } = useTranslation('common')
  const { user, logout } = useSession()
  if (!user) {
    return null
  }

  const name = user.displayName || user.email
  const tenant = user.customerName || user.tenantId

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label={name}>
          <Avatar className="size-8">
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium">{name}</span>
          {user.displayName && (
            <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          )}
          {tenant && (
            <span className="text-muted-foreground truncate text-xs font-normal">
              {t('ui.menu.tenant')}: {tenant}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void logout()
          }}
        >
          <LogOut className="size-4" />
          {t('ui.button.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
